import clsx from 'clsx';
import type { AnchorHTMLAttributes, FunctionComponent, PropsWithChildren } from 'react';

import { Icon, type IconicNames, type IconicTypes } from '@clubmed/trident-icons';
import { twMerge } from './helpers/twMerge';

interface LinkProps<T extends HTMLAnchorElement = HTMLAnchorElement>
  extends AnchorHTMLAttributes<T> {
  /**
   * Label text
   */
  label: string;
  /**
   * Icon name
   */
  icon?: IconicNames;
  iconType?: IconicTypes;
  /**
   * Icon width
   */
  width?: string;
  /**
   * Underlined
   */
  underlined?: boolean;
  /**
   * Additional class names
   */
  className?: string;
  /**
   * Is the link inert (not itself clickable but part of a clickable element)
   */
  inert?: boolean;
  /**
   * Allow giving a custom component
   */
  component?: FunctionComponent<PropsWithChildren<any>> | string;
}

export const Link: FunctionComponent<LinkProps> = ({
  label,
  icon,
  component = 'a',
  iconType,
  underlined = true,
  className,
  inert,
  width = '24px',
  ...anchorAttrs
}) => {
  const lastSpace = label.lastIndexOf(' ');
  const lastIndex = lastSpace === -1 ? label.length : lastSpace;

  const first = label.substring(0, 1);
  const middle = label.substring(1, lastIndex + 1);
  const last = label.substring(lastIndex);
  const Cmp = component as FunctionComponent<PropsWithChildren>;

  const TagName = inert ? 'span' : Cmp;

  return (
    <TagName
      className={twMerge('decoration-none link-container cursor-pointer text-inherit', className)}
      data-name="Link"
      {...anchorAttrs}
    >
      <span className={clsx({ 'link-underline': underlined })}>{first}</span>
      <span
        className={clsx('hoverable', {
          'has-icon': icon,
          'link-underline': underlined,
        })}
      >
        {middle}
        <span className="inline-block">
          {last}
          {icon && (
            <Icon
              name={icon}
              iconType={iconType}
              width={width}
              style={{ marginInlineStart: '8px' }}
            />
          )}
        </span>
      </span>
    </TagName>
  );
};
